import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
	inject,
	Injectable,
	InjectionToken,
	TransferState,
} from '@angular/core';

import { firstValueFrom, map, Observable, take } from 'rxjs';

import { HOTEL_SLUG } from '@valk-nx/core/lib/core';
import {
	HALResponse,
	stripHAL,
} from '@valk-nx/core/lib/interfaces/hal.interface';
import { ContentHotelInterface } from '@valk-nx/core/lib/interfaces/hotels.interface';
import { fetchWithTransferState } from '@valk-nx/core/lib/ssr/ssr.helper';

import {
	DealData,
	HotelDealData,
	MeetingRoomResponseData,
	MiniHotelSiteData,
	PackageData,
	PromotionCodeInterface,
	RoomData,
	SimpleHotelDealData,
	ThemePackageData,
} from './content.interface';

export const CONTENT_SERVICE_URL = new InjectionToken<string>(
	'content-service-url',
);
export const CONTENT_API_COOKIES = new InjectionToken<string[]>(
	'content-api-cookies',
);

@Injectable({ providedIn: 'root' })
export class ContentService {
	private readonly http = inject(HttpClient);
	private readonly serviceUrl = inject(CONTENT_SERVICE_URL, {
		optional: true,
	});
	private readonly apiCookies = inject<{
		withCredentials: boolean;
	}>(CONTENT_API_COOKIES);
	private readonly hotelSlug = inject(HOTEL_SLUG);
	private readonly transferState = inject(TransferState);

	private fetchWithCache<T>(
		path: string,
		key: string,
		language: string,
		extraParams: string | null = null,
	): Observable<T> {
		const headers = new HttpHeaders({ 'Accept-Language': language });
		const params = extraParams ? `&${extraParams}` : '';
		const url = `${this.serviceUrl}${path}?language=${language}${params}`;

		return fetchWithTransferState<T>(
			this.transferState,
			this.http,
			key,
			url,
			{
				headers,
				...this.apiCookies,
			},
		);
	}

	getRooms(language: string) {
		return this.fetchWithCache<HALResponse<RoomData[]>>(
			`hotels/${this.hotelSlug}/rooms`,
			`hotels/${this.hotelSlug}/rooms?language=${language}`,
			language,
		);
	}

	getRoom(roomSlug: string, language: string) {
		return this.fetchWithCache<HALResponse<RoomData>>(
			`hotels/${this.hotelSlug}/rooms/${roomSlug}`,
			`hotels/${this.hotelSlug}/rooms/${roomSlug}?language=${language}`,
			language,
		);
	}

	getPackage(packageSlug: string, language: string) {
		return this.fetchWithCache<HALResponse<PackageData>>(
			`hotels/${this.hotelSlug}/package-batches/${packageSlug}`,
			`hotels/${this.hotelSlug}/package-batches/${packageSlug}?language=${language}`,
			language,
		);
	}

	getPackages(language: string) {
		return this.fetchWithCache<HALResponse<PackageData[]>>(
			`hotels/${this.hotelSlug}/package-batches`,
			`hotels/${this.hotelSlug}/package-batches?language=${language}&filtered=true`,
			language,
			'filtered=true',
		);
	}

	getMeetingRoom(meetingRoomSlug: string, language: string) {
		return this.fetchWithCache<HALResponse<MeetingRoomResponseData>>(
			`hotels/${this.hotelSlug}/meeting-rooms/${meetingRoomSlug}`,
			`hotels/${this.hotelSlug}/meeting-rooms/${meetingRoomSlug}?language=${language}`,
			language,
		);
	}

	getMeetingRooms(language: string) {
		return this.fetchWithCache<HALResponse<MeetingRoomResponseData[]>>(
			`hotels/${this.hotelSlug}/meeting-rooms`,
			`hotels/${this.hotelSlug}/meeting-rooms?language=${language}`,
			language,
		);
	}

	getHotelInfo(language: string) {
		return this.fetchWithCache<HALResponse<ContentHotelInterface>>(
			`hotels/${this.hotelSlug}`,
			`hotels/${this.hotelSlug}?language=${language}`,
			language,
		);
	}

	getMiniHotelInfo(hotelSlug: string, type: string, language: string) {
		return this.fetchWithCache<HALResponse<MiniHotelSiteData>>(
			`hotels/mini-hotelsite/${hotelSlug}/${type}`,
			`hotels/mini-hotelsite/${hotelSlug}/${type}?language=${language}`,
			language,
		);
	}

	getHotelsByFacilities(facilityGuids: string[], language: string) {
		const facilityGuidsString = facilityGuids.length
			? `&facilityGuids=${facilityGuids.join(',')}`
			: '';
		const facilityGuidsParams = facilityGuids.length
			? `facilityGuids=${facilityGuids.join(',')}`
			: null;

		return this.fetchWithCache<HALResponse<SimpleHotelDealData[]>>(
			`hotels/by-facilities`,
			`hotels/by-facilities?language=${language}${facilityGuidsString}`,
			language,
			facilityGuidsParams,
		).pipe(stripHAL);
	}

	getDeal(dealSlug: string, language: string) {
		return this.fetchWithCache<HALResponse<DealData>>(
			`deals/${dealSlug}/hotels`,
			`deals/${dealSlug}/hotels?language=${language}`,
			language,
		);
	}

	getHotelByDeal(dealSlug: string, hotelSlug: string, language: string) {
		return this.fetchWithCache<HALResponse<HotelDealData>>(
			`deals/${dealSlug}/hotels/${hotelSlug}`,
			`deals/${dealSlug}/hotels/${hotelSlug}?language=${language}`,
			language,
		);
	}

	getLegal(keyParam: string, language: string) {
		const hotelSlugKeyParam = this.hotelSlug
			? `&hotel-slug=${this.hotelSlug}`
			: '';
		const hotelSlugExtraParam = this.hotelSlug
			? `hotel-slug=${this.hotelSlug}`
			: null;

		return this.fetchWithCache<HALResponse<string>>(
			`legal/${keyParam}`,
			`legal/${keyParam}?language=${language}${hotelSlugKeyParam}`,
			language,
			hotelSlugExtraParam,
		);
	}

	getThemePackages(
		themeGuids: string[],
		hotelGuids: string[],
		language: string,
	): Observable<HALResponse<ThemePackageData[]>> {
		const themeGuidsString = themeGuids.length
			? `&themeGuids=${themeGuids.join(',')}`
			: '';
		const hotelGuidsString = hotelGuids.length
			? `&hotelGuids=${hotelGuids.join(',')}`
			: '';

		return this.fetchWithCache<HALResponse<ThemePackageData[]>>(
			`package-batches`,
			`package-batches?language=${language}&filtered=true${themeGuidsString}${hotelGuidsString}`,
			language,
			`filtered=true${themeGuidsString}${hotelGuidsString}`,
		);
	}

	submitPromotionCode(
		promotionCode: string,
		language: string,
		recaptchaToken: string,
		recaptchaVersion: 'v2' | 'v3' = 'v3',
	): Promise<PromotionCodeInterface> {
		const headers = new HttpHeaders({
			'Accept-Language': language,
		});

		return firstValueFrom(
			this.http
				.post<{ data: PromotionCodeInterface }>(
					`${this.serviceUrl}promotions/${promotionCode}/hotels`,
					{
						language,
						token: recaptchaToken,
						recaptchaVersion,
					},
					{
						headers,
						...this.apiCookies,
					},
				)
				.pipe(
					take(1),
					map((response) => response.data),
				),
		);
	}
}
